import TimeSeries, { Key, Line, Range } from '@/components/TimeSeries';
import { useTimeSeriesReducer } from '@/components/TimeSeries/hooks';
import { toCurrencyTruncated } from '@simplywallst/formatting';
import { useEffect, useMemo } from 'react';
import { useTheme, Text } from '@simplywallst/ui-core';
import { Dot, Table, StyledTd } from './styled';
import { Now } from '@/components/TimeSeries/components/Markers/styled';
import {
  formatterCurrencyTruncated,
  formatterPercentage,
} from '@/utilities/format';
import { format, subYears, subDays, addYears } from 'date-fns';
import { getTimeSeriesPeriodMap } from '@/utilities/timeSeriesPeriodMap';
import { useChartDefinitions } from '@/components/ChartDefinitionsContext';
import {
  getPriceGrowthData,
  getGrowthColor,
  isPositiveMarker,
  getValidPrice,
} from '../../utils';
import {
  dispersionThreshold,
  growthThreshold,
  daysBack,
  minimumPriceTargets,
} from '../../constants';
import type { SelectedStatement } from '@/pages/CompanyReport/redux/interface';
import type { PriceTargetData } from '../../queryPriceTargets';

import { VisxText } from '@/components/VisxText';

enum Series {
  series01 = 'price',
  series02 = 'priceTarget',
  rangeTargetHigh = 'priceTargetRangeHigh',
  rangeTargetLow = 'priceTargetRangeLow',
}
interface props {
  statement: SelectedStatement;
  currencyISO: string;
  useNewCalcFlag: boolean;
  priceTargetData: number[][];
  priceTargetKeys: number[];
  analystData: PriceTargetData['analystData'];
  priceTargetMax: number;
  priceTargetMin: number;
  priceTargetRangeHigh: number[][];
  priceTargetRangeLow: number[][];
  priceData: number[][];
  priceMax: number;
  priceMin: number;
  dayZero: number;
}
export const AnalystsPriceTargetsChart = ({
  statement,
  currencyISO,
  useNewCalcFlag,
  priceTargetData,
  priceTargetKeys,
  analystData,
  priceTargetMax,
  priceTargetMin,
  priceTargetRangeHigh,
  priceTargetRangeLow,
  priceData,
  priceMax,
  priceMin,
  dayZero,
}: props) => {
  const theme = useTheme();
  const { chart05Gradient02, chartNEGGradient02, chartPOSGradient02 } =
    useChartDefinitions();
  const defaultFilter = {
    price: true,
    priceTarget: true,
    priceTargetRangeHigh: true,
    priceTargetRangeLow: true,
  };
  const periodMap = getTimeSeriesPeriodMap();
  const [targetData, targetKeys, analystMetaData] = useMemo(() => {
    const date = new Date(periodMap['2Y']);
    const month = date.getMonth();
    const year = date.getFullYear();
    const utcDate = Date.UTC(year, month, 1);
    const startDate = subDays(utcDate, 1).getTime();
    const endDate = addYears(utcDate, 3).getTime();
    const targetPriceDataDefault = [
      [startDate, -1],
      [endDate, -1],
    ];
    const targetPriceKeysDefault = [startDate, endDate];
    const analystDataDefault = {
      [startDate]: { dispersion: 0, analystCount: 0 },
      [endDate]: { dispersion: 0, analystCount: 0 },
    };
    //Return default values when analyst data is not available to render chart's default state
    if (
      statement.value === 'No data' ||
      priceTargetData.length < minimumPriceTargets
    )
      return [
        targetPriceDataDefault,
        targetPriceKeysDefault,
        analystDataDefault,
      ];
    return [priceTargetData, priceTargetKeys, analystData];
  }, [
    priceTargetData,
    priceTargetKeys,
    analystData,
    periodMap,
    statement.value,
  ]);

  const seriesData = useMemo(
    () => ({
      [Series.series01]: priceData,
      [Series.series02]: targetData,
      [Series.rangeTargetHigh]: priceTargetRangeHigh,
      [Series.rangeTargetLow]: priceTargetRangeLow,
    }),
    [priceData, targetData, priceTargetRangeHigh, priceTargetRangeLow]
  );
  const colors = {
    [Series.series01]: {
      solid: theme.color.chart01,
      opaque: theme.color.chart01RGBA(0.5),
    },
    [Series.series02]: {
      solid: theme.color.chart05,
      opaque: chart05Gradient02,
    },
  };
  const seriesLabel = {
    [Series.series01]: 'Share Price',
    [Series.series02]: 'Average 1Y Price Target',
  };

  const {
    entities,
    keys,
    minimumX,
    maximumX,
    periods,
    series,
    filter,
    disabled,
    dispatch,
  } = useTimeSeriesReducer<Series>({
    data: seriesData,
    dayZero,
    defaultFilter,
    hideToggleFilter: false,
  });
  useEffect(() => {
    dispatch({
      type: 'reset',
      payload: {
        data: seriesData,
        dayZero,
        defaultFilter,
      },
    });
  }, [dispatch, seriesData, dayZero]);

  const minMaxY = {
    [Series.series01]: [priceMin, priceMax],
    [Series.series02]: [priceTargetMin, priceTargetMax],
  };
  const filterSeries = (n: string) =>
    n === Series.series01 || n === Series.series02;

  const minimumY = Math.min(
    ...series
      .filter(filterSeries)
      .filter((n) => filter[n])
      .map((n) => minMaxY[n][0])
  );
  const maximumY = Math.max(
    ...series
      .filter(filterSeries)
      .filter((n) => filter[n])
      .map((n) => minMaxY[n][1])
  );

  return (
    <TimeSeries
      keys={targetKeys}
      filter={filter}
      disabled={disabled}
      series={series}
      periods={periods}
      entities={entities}
      minimumX={minimumX}
      maximumX={maximumX}
      minimumY={minimumY}
      maximumY={maximumY}
      moveWithTrailingYear
      pastLabel="Past"
      futureLabel="12m Forecast"
      formatYAxis={(value) => toCurrencyTruncated(value, 2, currencyISO)}
      dataTable={({ key: date }) => {
        if (typeof date !== 'number' || entities[date] === undefined)
          return <></>;
        const oneYearAgo = subYears(date, 1).getTime();
        const price = getValidPrice(oneYearAgo, priceData, daysBack);
        const priceTarget = entities[date].priceTarget;
        const dispersion = analystMetaData[date].dispersion;
        const analystCount = analystMetaData[date].analystCount;
        const dispersionLevel =
          dispersion <= dispersionThreshold ? 'Good' : 'Low';
        const { growth, growthSign } = getPriceGrowthData(
          price,
          priceTarget,
          useNewCalcFlag
        );
        return (
          <Table data-cy-id="analyst-price-targets-tooltip">
            <thead>
              <tr>
                <th colSpan={2}>
                  {oneYearAgo ? format(oneYearAgo, 'MMM dd yyyy') : `n/a`}
                </th>
                <th>{analystCount} Analysts</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Share Price</td>
                <StyledTd color={theme.color.brand01} colSpan={2}>
                  {formatterCurrencyTruncated(price, currencyISO)}
                </StyledTd>
              </tr>
              <tr>
                <td>
                  Average 1Y Price
                  <br />
                  Target
                </td>
                {priceTarget >= 0 ? (
                  <StyledTd color={theme.color.chart05}>
                    {formatterCurrencyTruncated(priceTarget, currencyISO)}
                  </StyledTd>
                ) : (
                  <td>N/A</td>
                )}
                <StyledTd color={getGrowthColor(growth, theme)}>
                  {price !== null &&
                    priceTarget >= 0 &&
                    growthSign + formatterPercentage(growth)}
                </StyledTd>
              </tr>
              <tr>
                <td>Agreement</td>
                <td colSpan={2}>
                  {priceTarget >= 0 ? (
                    <>
                      <Text
                        typography="tiny"
                        color={dispersionLevel === 'Good' ? 'good' : 'bad'}
                      >
                        {dispersionLevel}
                      </Text>
                      <Text typography="tiny" color="whiteRGBA" opacity={0.5}>
                        Analysts agreement range is spread
                        <br />
                        {dispersionLevel === 'Good' ? 'less' : 'more'} than 15%
                        from the average
                      </Text>
                    </>
                  ) : (
                    <Text typography="tiny" color="whiteRGBA" opacity={0.7}>
                      N/A
                    </Text>
                  )}
                </td>
              </tr>
            </tbody>
          </Table>
        );
      }}
      nowMarker={({ x, height, hover }) => (
        <Now
          y="0"
          x={x}
          width="1"
          height={24 + height}
          fill={colors[Series.series02].solid}
          hover={hover}
        />
      )}
      marker={({ key: date, xScale, yScale }) => {
        if (typeof date !== 'number' || entities[date] === undefined)
          return <></>;
        const oneYearAgo = subYears(date, 1).getTime();
        const validPrice = getValidPrice(oneYearAgo, priceData, daysBack);
        const priceTarget = entities[date].priceTarget;
        if (validPrice === null || priceTarget < 0) return <></>;
        const xCoordTargetPrice = xScale(date);
        const yCoordTargetPrice = yScale(priceTarget);
        const xCoordPrice = xScale(oneYearAgo);
        const yCoordPrice = yScale(validPrice);
        const yCoordTargetHigh = yScale(entities[date].priceTargetRangeHigh);
        const yCoordTargetLow = yScale(entities[date].priceTargetRangeLow);
        const { growth } = getPriceGrowthData(
          validPrice,
          priceTarget,
          useNewCalcFlag
        );
        const isPositiveValue = isPositiveMarker(
          analystMetaData[date].dispersion,
          dispersionThreshold,
          growth,
          growthThreshold,
          useNewCalcFlag
        );
        const beamCoords = `${xCoordPrice} ${yCoordPrice}, ${xCoordTargetPrice} ${yCoordTargetHigh}, ${xCoordTargetPrice}, ${yCoordTargetLow}`;
        return (
          <>
            <line
              x1={xCoordPrice}
              y1={yCoordPrice}
              x2={xCoordTargetPrice}
              y2={yCoordTargetPrice}
              stroke={isPositiveValue ? theme.color.good : theme.color.bad}
              strokeWidth={2}
              strokeDasharray="5, 5"
            />
            <circle
              key={`data-point-target-${series}`}
              transform={`translate(
                      ${xCoordTargetPrice},
                      ${yCoordTargetPrice})`}
              r="4"
              fill={colors[Series.series02].solid}
              stroke="white"
              strokeWidth="1"
            />
            <circle
              key={`data-point-price-${series}`}
              transform={`translate(
                ${xCoordPrice},
                ${yCoordPrice})`}
              r="4"
              fill={colors[Series.series01].solid}
              stroke="white"
              strokeWidth="1"
            />
            <polygon
              points={beamCoords}
              overflow="visible"
              fill={isPositiveValue ? chartPOSGradient02 : chartNEGGradient02}
            />
          </>
        );
      }}
      legend={() =>
        series.filter(filterSeries).map((series) => {
          return (
            <Key key={`timeseries-key-${series}`} active={filter[series]}>
              <Dot
                color={
                  colors[series].solid ? colors[series].solid : colors[series]
                }
                active={filter[series]}
              />
              <span>{seriesLabel[series]}</span>
            </Key>
          );
        })
      }
    >
      {({ keys: dates, xScale, yScale, entities, chartWidth, chartHeight }) =>
        series
          .filter((n) => filter[n])
          .map((key) => {
            if (entities[dates[1]] === undefined) return <></>;
            const priceTarget = entities[dates[1]].priceTarget;
            const todayCoord = xScale(periodMap.NOW);
            const nextYearCoord = xScale(dates[1]);
            const x =
              todayCoord && nextYearCoord
                ? (todayCoord + nextYearCoord) / 2
                : chartWidth - 50;
            const y = chartHeight / 2 + 24;
            return (
              <g key={`chart-${key}`}>
                {key === Series.series02 && priceTarget >= 0 && (
                  <Range
                    data={keys.filter(
                      (d) =>
                        entities[d][Series.rangeTargetHigh] !== undefined &&
                        entities[d][Series.rangeTargetLow] !== undefined
                    )}
                    xSelect={(d) => d}
                    ySelectHigh={(d) => entities[d][Series.rangeTargetHigh]}
                    ySelectLow={(d) => entities[d][Series.rangeTargetLow]}
                    xScale={xScale}
                    yScale={yScale}
                    fillPos={colors[Series.series02].opaque}
                    fillNeg={colors[Series.series02].opaque}
                  />
                )}
                {filterSeries(key) && (
                  <Line
                    data={keys.filter((d) => entities[d][key] !== undefined)}
                    xSelect={(d) => d}
                    ySelect={(d) => (entities[d] ? entities[d][key] : 0)}
                    xScale={xScale}
                    yScale={yScale}
                    strokePos={
                      key === Series.series01
                        ? colors[key].opaque
                        : colors[key].solid
                    }
                    strokeNeg={theme.color.bad}
                  />
                )}
                {priceTarget < 0 && (
                  <VisxText
                    x={x}
                    y={y}
                    verticalAnchor={'start'}
                    textAnchor={'middle'}
                    fill={theme.color.white}
                    typography="tiny"
                  >
                    Insufficient Data
                  </VisxText>
                )}
              </g>
            );
          })
      }
    </TimeSeries>
  );
};
